import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { DateTime } from 'luxon';
import StatDateRange, { dubaiToday, shiftDays, useRangeParams } from './StatDateRange';
import { colorOf } from '../../../constants/areas';
import './statCharts.css';
import './propertiesStat.css';

/**
 * Property insights (owner, 2026-10-11): "how many building in each area, how
 * many studio one bedroom 2 bedrooms etc, booking frequency in each area …
 * to kinda see what works the best on airbnb".
 *
 * Every All Inclusive Package clean is a guest changeover, so how many of
 * those a unit gets is how often it is booked. The page reads that off our
 * own portfolio by area, size, capacity and building — the market telling us
 * which units it keeps filling. Figures come from GET /api/statistics/properties;
 * the definitions are spelled out at the bottom of the page.
 *
 * Chart rules (dataviz): one axis per chart, a fixed categorical palette,
 * thin marks, a table behind every chart, and a refetch that dims the last
 * render instead of flashing a skeleton.
 */
const SIZE_COLORS = { studio: '#2a78d6', '1br': '#eb6834', '2br': '#1baf7a', '3br': '#eda100', '4br+': '#4a3aa7', unknown: '#b3b1aa' };
const SLEEPS_COLORS = { '2': '#2a78d6', '3-4': '#eb6834', '5-6': '#1baf7a', '7+': '#eda100', unknown: '#b3b1aa' };
// Stable defaults: a new object per render would defeat the memo in useRangeParams.
const RANGE_DEFAULTS = { from: shiftDays(dubaiToday(), -89), to: dubaiToday() };

const n1 = (v) => (v == null ? '—' : Number(v).toFixed(1));
const n0 = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US'));
const everyDays = (d) => (d == null ? '—' : `every ${n1(d)} days`);
const fmtMonth = (ym) => DateTime.fromFormat(ym, 'yyyy-MM').toFormat('MMM yy');

/** Horizontal bars for one figure across a few rows; the exact value beside each. */
function BarRows({ rows, value, label, color, note, max: maxIn }) {
    const max = maxIn ?? Math.max(0, ...rows.map((r) => Number(value(r)) || 0));
    return (
        <div className="sc-barRows">
            {rows.map((r, i) => {
                const v = Number(value(r)) || 0;
                return (
                    <div className="sc-barRow" key={r.key || r.label || i}>
                        <div className="sc-barName">
                            <span className="sc-swatch" style={{ background: color(r) }} />
                            <span>{label(r)}</span>
                        </div>
                        <div className="sc-barTrack">
                            <div className="sc-barFill" style={{ width: `${max > 0 ? (v / max) * 100 : 0}%`, background: color(r) }} />
                        </div>
                        <div className="sc-barVal">{n1(v)}{note ? <small>{note(r)}</small> : null}</div>
                    </div>
                );
            })}
        </div>
    );
}

function MonthTip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    const r = payload[0].payload;
    return (
        <div className="sc-tip">
            <div className="sc-tipTitle">{fmtMonth(label)}{r.part ? <span className="sc-tipTotal"> · part month</span> : null}</div>
            <div className="sc-tipRow"><span className="sc-tipKey">Per unit, per 30 days</span><span className="sc-tipVal">{n1(r.per_unit_30d)}</span></div>
            <div className="sc-tipRow"><span className="sc-tipKey">Turnovers</span><span className="sc-tipVal">{n0(r.turnovers)}</span></div>
            <div className="sc-tipRow"><span className="sc-tipKey">Units with a turnover</span><span className="sc-tipVal">{n0(r.worked_units)}</span></div>
            <div className="sc-tipRow"><span className="sc-tipKey">Days counted</span><span className="sc-tipVal">{r.days}</span></div>
        </div>
    );
}

const AREA_SORTS = [
    { key: 'units', label: 'Most units', cmp: (a, b) => b.units - a.units || b.turnovers - a.turnovers },
    { key: 'busiest', label: 'Busiest per unit', cmp: (a, b) => b.per_unit_30d - a.per_unit_30d || b.turnovers - a.turnovers },
    { key: 'turnovers', label: 'Most turnovers', cmp: (a, b) => b.turnovers - a.turnovers || b.units - a.units },
];
const BUILDING_LISTS = [
    { key: 'busiest', label: 'Busiest per unit', note: 'at least two units with a turnover' },
    { key: 'most_turnovers', label: 'Most turnovers' },
    { key: 'largest', label: 'Most units' },
];

export default function PropertiesStat() {
    const [range, setRange] = useRangeParams(RANGE_DEFAULTS);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [areaSort, setAreaSort] = useState('units');
    const [buildingList, setBuildingList] = useState('busiest');

    useEffect(() => {
        let alive = true;
        setLoading(true); setError('');
        axios.get('/api/statistics/properties', { params: { from: range.from || undefined, to: range.to || undefined } })
            .then((res) => { if (alive) setData(res.data); })
            .catch((e) => { if (alive) setError(e.response?.data?.error || 'Could not load property insights.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [range]);

    const t = data?.totals || {};
    const areas = useMemo(() => {
        const cmp = AREA_SORTS.find((s) => s.key === areaSort)?.cmp || AREA_SORTS[0].cmp;
        return [...(data?.by_area || [])].sort(cmp);
    }, [data, areaSort]);
    const buildings = data?.top_buildings?.[buildingList] || [];
    // The bars compare real sizes; "not set" is a data gap and stays in the
    // table only (three units with no beds saved once topped the chart).
    const sizes = (data?.by_size || []).filter((s) => s.units > 0 && s.key !== 'unknown');
    const sleeps = (data?.by_sleeps || []).filter((s) => s.units > 0 && s.key !== 'unknown');
    const weekdayMax = Math.max(0, ...(data?.by_weekday || []).map((d) => d.turnovers));
    const peakTotal = (t.peak_starts || 0) + (t.off_peak_starts || 0);

    return (
        <div className="sc-view prst">
            <StatDateRange value={range} onChange={setRange} allTime loading={loading} />
            {loading && data && <div className="sc-updating" role="status">Updating the figures for this range…</div>}
            {error && <div className="sc-empty">{error}</div>}
            {!error && !data && <div className="sc-empty">Loading…</div>}
            {data && (
                <div className={loading ? 'sc-dim' : ''}>
                    <div className="sc-tiles">
                        <div className="sc-tile">
                            <div className="sc-tileLabel">Active units</div>
                            <div className="sc-tileValue">{n0(t.active_units)}</div>
                            <div className="sc-tileSub">{n0(t.buildings)} buildings · {n0(t.areas)} areas · {n0(t.clients)} clients</div>
                        </div>
                        <div className="sc-tile sc-tile--hero">
                            <div className="sc-tileLabel">Turnovers per unit, per 30 days</div>
                            <div className="sc-tileValue">{n1(t.per_unit_30d)}</div>
                            <div className="sc-tileSub">a unit turns over {everyDays(t.every_days)} · {n0(t.worked_units)} units had a turnover</div>
                        </div>
                        <div className="sc-tile">
                            <div className="sc-tileLabel">Turnovers</div>
                            <div className="sc-tileValue">{n0(t.turnovers)}</div>
                            <div className="sc-tileSub">{n0(t.cleanings)} cleanings in total · {data.range.days} days</div>
                        </div>
                        <div className="sc-tile">
                            <div className="sc-tileLabel">Peak-time starts</div>
                            <div className="sc-tileValue">{t.peak_share == null ? '—' : `${n1(t.peak_share)}%`}</div>
                            <div className="sc-tileSub">start before 3 pm · {n0(t.off_peak_starts)} off-peak</div>
                        </div>
                    </div>

                    {(t.no_area_units > 0 || t.no_size_units > 0) && (
                        <div className="sc-warn">
                            {t.no_area_units > 0 ? `${n0(t.no_area_units)} active units have no area saved. ` : ''}
                            {t.no_size_units > 0 ? `${n0(t.no_size_units)} active units have no bedroom count. ` : ''}
                            They are counted in the totals but cannot be placed in the tables below — fix the property, not the page.
                        </div>
                    )}

                    <div className="sc-grid2">
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <h3 className="sc-cardTitle">Which size works hardest</h3>
                                <span className="sc-cardSub">turnovers per unit, per 30 days</span>
                            </div>
                            <BarRows
                                rows={sizes}
                                value={(r) => r.per_unit_30d}
                                label={(r) => r.label}
                                color={(r) => SIZE_COLORS[r.key] || SIZE_COLORS.unknown}
                                note={(r) => ` · ${n0(r.units)} units`}
                            />
                            <div className="sc-tableWrap prst__tableGap">
                                <table className="sc-table">
                                    <thead><tr><th></th><th>Size</th><th className="sc-num">Units</th><th className="sc-num">Share</th><th className="sc-num">Turnovers</th><th className="sc-num">Share</th><th className="sc-num">Turns over</th></tr></thead>
                                    <tbody>
                                        {(data.by_size || []).map((r) => (
                                            <tr key={r.key} className={r.units > 0 ? '' : 'sc-rowMuted'}>
                                                <td className="sc-swatchCell"><span className="sc-swatch" style={{ background: SIZE_COLORS[r.key] || SIZE_COLORS.unknown }} /></td>
                                                <td>{r.label}</td>
                                                <td className="sc-num"><b>{n0(r.units)}</b></td>
                                                <td className="sc-num">{r.share_units}%</td>
                                                <td className="sc-num">{n0(r.turnovers)}</td>
                                                <td className="sc-num">{r.share_turnovers}%</td>
                                                <td className="sc-num">{everyDays(r.every_days)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <h3 className="sc-cardTitle">How many a unit sleeps</h3>
                                <span className="sc-cardSub">from the beds saved on each property</span>
                            </div>
                            <BarRows
                                rows={sleeps}
                                value={(r) => r.per_unit_30d}
                                label={(r) => r.label}
                                color={(r) => SLEEPS_COLORS[r.key] || SLEEPS_COLORS.unknown}
                                note={(r) => ` · ${n0(r.units)} units`}
                            />
                            <div className="sc-tableWrap prst__tableGap">
                                <table className="sc-table">
                                    <thead><tr><th></th><th>Capacity</th><th className="sc-num">Units</th><th className="sc-num">Turnovers</th><th className="sc-num">Per unit / 30d</th><th className="sc-num">Turns over</th></tr></thead>
                                    <tbody>
                                        {(data.by_sleeps || []).map((r) => (
                                            <tr key={r.key} className={r.units > 0 ? '' : 'sc-rowMuted'}>
                                                <td className="sc-swatchCell"><span className="sc-swatch" style={{ background: SLEEPS_COLORS[r.key] || SLEEPS_COLORS.unknown }} /></td>
                                                <td>{r.label}</td>
                                                <td className="sc-num"><b>{n0(r.units)}</b></td>
                                                <td className="sc-num">{n0(r.turnovers)}</td>
                                                <td className="sc-num">{n1(r.per_unit_30d)}</td>
                                                <td className="sc-num">{everyDays(r.every_days)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="sc-note">A double or super king bed sleeps two, a single bed one. Sofa beds are not recorded.</div>
                        </div>
                    </div>

                    <div className="sc-card">
                        <div className="sc-cardHead">
                            <h3 className="sc-cardTitle">Areas</h3>
                            <div className="sc-cardTools">
                                {AREA_SORTS.map((s) => (
                                    <button key={s.key} type="button" className={`sc-toggle${areaSort === s.key ? ' is-active' : ''}`} onClick={() => setAreaSort(s.key)}>{s.label}</button>
                                ))}
                            </div>
                        </div>
                        <div className="sc-tableWrap">
                            <table className="sc-table prst__areas">
                                <thead>
                                    <tr>
                                        <th></th><th>Area</th>
                                        <th className="sc-num">Buildings</th><th className="sc-num">Units</th>
                                        <th className="sc-num">Studio</th><th className="sc-num">1 bed</th><th className="sc-num">2 bed</th><th className="sc-num">3 bed+</th>
                                        <th className="sc-num">Turnovers</th><th className="sc-num">Per unit / 30d</th><th className="sc-num">Turns over</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {areas.map((a) => (
                                        <tr key={a.area} className={a.units > 0 ? '' : 'sc-rowMuted'}>
                                            <td className="sc-swatchCell"><span className="sc-swatch" style={{ background: colorOf(a.area) }} /></td>
                                            <td>{a.area}</td>
                                            <td className="sc-num">{n0(a.buildings)}</td>
                                            <td className="sc-num"><b>{n0(a.units)}</b></td>
                                            <td className="sc-num">{a.sizes.studio || '·'}</td>
                                            <td className="sc-num">{a.sizes['1br'] || '·'}</td>
                                            <td className="sc-num">{a.sizes['2br'] || '·'}</td>
                                            <td className="sc-num">{(a.sizes['3br'] + a.sizes['4br+']) || '·'}</td>
                                            <td className="sc-num">{n0(a.turnovers)}</td>
                                            <td className="sc-num"><b>{n1(a.per_unit_30d)}</b></td>
                                            <td className="sc-num">{everyDays(a.every_days)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td></td><td>All areas</td>
                                        <td className="sc-num">{n0(t.buildings)}</td><td className="sc-num">{n0(t.active_units)}</td>
                                        <td className="sc-num">{n0(sumSize(data.by_area, 'studio'))}</td>
                                        <td className="sc-num">{n0(sumSize(data.by_area, '1br'))}</td>
                                        <td className="sc-num">{n0(sumSize(data.by_area, '2br'))}</td>
                                        <td className="sc-num">{n0(sumSize(data.by_area, '3br') + sumSize(data.by_area, '4br+'))}</td>
                                        <td className="sc-num">{n0(t.turnovers)}</td><td className="sc-num">{n1(t.per_unit_30d)}</td><td className="sc-num">{everyDays(t.every_days)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                        <div className="sc-note">"Per unit" divides by the units that had at least one turnover in the period, so a unit added last week does not drag its area down.</div>
                    </div>

                    <div className="sc-grid2">
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <h3 className="sc-cardTitle">Best area and size together</h3>
                                <span className="sc-cardSub">at least three units with a turnover</span>
                            </div>
                            {data.combos?.length ? (
                                <ol className="prst__ranked">
                                    {data.combos.map((c, i) => (
                                        <li key={`${c.area}|${c.size}`} className="prst__rankRow">
                                            <span className="prst__rank">{i + 1}</span>
                                            <span className="sc-swatch" style={{ background: colorOf(c.area) }} />
                                            <span className="prst__rankName"><b>{c.area}</b> · {c.size_label}</span>
                                            <span className="prst__rankMeta">{n0(c.units)} units</span>
                                            <span className="prst__rankVal">{n1(c.per_unit_30d)}<small>/30d</small></span>
                                        </li>
                                    ))}
                                </ol>
                            ) : <div className="sc-empty">Not enough turnovers in this period to rank combinations.</div>}
                        </div>

                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <h3 className="sc-cardTitle">Buildings</h3>
                                <div className="sc-cardTools">
                                    {BUILDING_LISTS.map((l) => (
                                        <button key={l.key} type="button" className={`sc-toggle${buildingList === l.key ? ' is-active' : ''}`} onClick={() => setBuildingList(l.key)}>{l.label}</button>
                                    ))}
                                </div>
                            </div>
                            {buildings.length ? (
                                <ol className="prst__ranked">
                                    {buildings.map((b, i) => (
                                        <li key={`${b.area}|${b.building}`} className="prst__rankRow">
                                            <span className="prst__rank">{i + 1}</span>
                                            <span className="sc-swatch" style={{ background: colorOf(b.area) }} />
                                            <span className="prst__rankName"><b>{b.building}</b> · {b.area}</span>
                                            <span className="prst__rankMeta">{n0(b.units)} {b.units === 1 ? 'unit' : 'units'} · {n0(b.turnovers)} turnovers</span>
                                            <span className="prst__rankVal">{n1(b.per_unit_30d)}<small>/30d</small></span>
                                        </li>
                                    ))}
                                </ol>
                            ) : <div className="sc-empty">No building has two units with a turnover in this period.</div>}
                            {BUILDING_LISTS.find((l) => l.key === buildingList)?.note && (
                                <div className="sc-note">{BUILDING_LISTS.find((l) => l.key === buildingList).note}</div>
                            )}
                        </div>
                    </div>

                    <div className="sc-card">
                        <div className="sc-cardHead">
                            <h3 className="sc-cardTitle">Month by month</h3>
                            <span className="sc-cardSub">
                                turnovers per unit, per 30 days
                                {data.by_month?.length ? ` · ${fmtMonth(data.by_month[0].month)} – ${fmtMonth(data.by_month[data.by_month.length - 1].month)}` : ''}
                                {data.by_month?.length === 24 && data.range.all_time ? ' (last 24 months)' : ''}
                            </span>
                        </div>
                        {data.by_month?.length ? (
                            <div className="sc-chart sc-chart--short">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={data.by_month} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="28%">
                                        <CartesianGrid stroke="#e1e0d9" vertical={false} />
                                        <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: '#898781' }} axisLine={{ stroke: '#c3c2b7' }} tickLine={false} minTickGap={12} />
                                        <YAxis tick={{ fontSize: 11, fill: '#898781' }} axisLine={false} tickLine={false} width={36} />
                                        <Tooltip cursor={{ fill: 'rgba(11,11,11,0.04)' }} content={<MonthTip />} />
                                        <Bar dataKey="per_unit_30d" fill="#2a78d6" stroke="#fcfcfb" strokeWidth={2} maxBarSize={34} radius={[4, 4, 0, 0]}>
                                            {data.by_month.map((m) => (
                                                <Cell key={m.month} fill={m.part ? '#9dc0ea' : '#2a78d6'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : <div className="sc-empty">No turnovers in this period.</div>}
                        <div className="sc-legend prst__legendGap">
                            <span className="sc-legendItem"><span className="sc-swatch" style={{ background: '#2a78d6' }} />Full month</span>
                            <span className="sc-legendItem"><span className="sc-swatch" style={{ background: '#9dc0ea' }} />Part month, scaled to 30 days</span>
                        </div>
                        <div className="sc-note">A part month is normalised on the days it has in the range. A few days scaled up is a fair rate but a noisy one — read the lighter bars loosely.</div>
                    </div>

                    <div className="sc-grid2">
                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <h3 className="sc-cardTitle">Which day guests check out</h3>
                                <span className="sc-cardSub">turnovers by weekday</span>
                            </div>
                            <div className="sc-barRows">
                                {(data.by_weekday || []).map((d) => (
                                    <div className="sc-barRow" key={d.isodow}>
                                        <div className="sc-barName"><span>{d.label}</span></div>
                                        <div className="sc-barTrack"><div className="sc-barFill" style={{ width: `${weekdayMax ? (d.turnovers / weekdayMax) * 100 : 0}%`, background: d.isodow >= 6 ? '#eb6834' : '#2a78d6' }} /></div>
                                        <div className="sc-barVal">{n0(d.turnovers)}<small>{d.share}%</small></div>
                                    </div>
                                ))}
                            </div>
                            <div className="sc-legend prst__legendGap">
                                <span className="sc-legendItem"><span className="sc-swatch" style={{ background: '#2a78d6' }} />Weekday</span>
                                <span className="sc-legendItem"><span className="sc-swatch" style={{ background: '#eb6834' }} />Weekend</span>
                            </div>
                        </div>

                        <div className="sc-card">
                            <div className="sc-cardHead">
                                <h3 className="sc-cardTitle">When the clean starts</h3>
                                <span className="sc-cardSub">peak before 3 pm, off-peak from 3 pm</span>
                            </div>
                            <div className="sc-share">
                                <div className="sc-shareBar" role="img" aria-label="Peak against off-peak starts">
                                    {peakTotal > 0 ? (
                                        <>
                                            <div className="sc-shareSeg" style={{ width: `${(t.peak_starts / peakTotal) * 100}%`, background: '#2a78d6' }} title={`Peak · ${n1(t.peak_share)}%`} />
                                            <div className="sc-shareSeg" style={{ width: `${(t.off_peak_starts / peakTotal) * 100}%`, background: '#eda100' }} title={`Off-peak · ${n1(100 - t.peak_share)}%`} />
                                        </>
                                    ) : <div className="sc-shareSeg sc-shareSeg--empty" />}
                                </div>
                                <table className="sc-table sc-shareTable">
                                    <thead><tr><th></th><th>Start</th><th className="sc-num">Turnovers</th><th className="sc-num">Share</th></tr></thead>
                                    <tbody>
                                        <tr><td className="sc-swatchCell"><span className="sc-swatch" style={{ background: '#2a78d6' }} /></td><td>Peak (before 3 pm)</td><td className="sc-num"><b>{n0(t.peak_starts)}</b></td><td className="sc-num">{peakTotal ? `${n1(t.peak_share)}%` : '—'}</td></tr>
                                        <tr><td className="sc-swatchCell"><span className="sc-swatch" style={{ background: '#eda100' }} /></td><td>Off-peak (from 3 pm)</td><td className="sc-num"><b>{n0(t.off_peak_starts)}</b></td><td className="sc-num">{peakTotal ? `${n1(100 - t.peak_share)}%` : '—'}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="sc-note">A same-day changeover has to start before the next guest arrives, so a high peak share means back-to-back bookings.</div>
                        </div>
                    </div>

                    <div className="sc-card prst__defs">
                        <div className="sc-cardHead"><h3 className="sc-cardTitle">How to read this page</h3></div>
                        <p><b>Turnover</b> = an All Inclusive Package clean (with or without deep cleaning): the changeover between one guest and the next. Hourly and deep cleans count as cleanings, not turnovers.</p>
                        <p><b>Per unit, per 30 days</b> = turnovers ÷ units that had at least one turnover ÷ days in the range × 30. <b>Turns over every N days</b> is the same figure the other way round.</p>
                        <p><b>Units, buildings and sizes</b> are today's active properties. <b>Turnovers</b> are everything in the range, including on units that have since been deactivated — the booking still happened in that area.</p>
                        <p>Cancelled, rejected and rescheduled-away bookings are left out. The demo accounts are never counted.</p>
                    </div>
                </div>
            )}
        </div>
    );
}

function sumSize(byArea, key) {
    return (byArea || []).reduce((s, a) => s + (a.sizes?.[key] || 0), 0);
}
